import React, { useState, useEffect } from "react";
import axios from "axios";
import openNotification from "components/utils/CustomNotification";
import "../../styles/GeneralConfigurations.css";
import { useLocation, useNavigate } from "react-router-dom";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";

const appSessionTimeoutToMinutes = (val: string) => {
  if (val.includes("minute")) return parseInt(val);
  if (val.includes("hour")) return parseInt(val) * 60;
  if (val.includes("day")) return parseInt(val) * 24 * 60;
  return 0;
};

const webSessionTimeoutToSeconds = (val: string) => {
  const parts = val.split(" ");
  const number = parseInt(parts[0], 10);

  if (val.includes("minute")) return number * 60;
  if (val.includes("hour")) return number * 3600;
  if (val.includes("day")) return number * 24 * 3600;
  return 0;
};

const minutesToHHMMSS = (mins: number) => {
  const h = Math.floor(mins / 60)
    .toString()
    .padStart(2, "0");
  const m = (mins % 60).toString().padStart(2, "0");
  return `${h}:${m}:00`;
};

const secondsToHHMMSS = (secs: number) => {
  const h = Math.floor(secs / 3600)
    .toString()
    .padStart(2, "0");
  const m = Math.floor((secs % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(secs % 60)
    .toString()
    .padStart(2, "0");
  return `${h}:${m}:${s}`;
};

const secondsToDHHMMSS = (secs: number) => {
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600)
    .toString()
    .padStart(2, "0");
  const m = Math.floor((secs % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(secs % 60)
    .toString()
    .padStart(2, "0");
  return d > 0 ? `${d}:${h}:${m}:${s}` : `${h}:${m}:${s}`;
};

// --- Type ---
interface ConfigItem {
  name: string;
  description: string;
  category: string;
  db_key: string;
  type: string;
  method: "toggle" | "input" | "dropdown";
  default: string;
  values: Record<string, string>;
  limits?: { lower: string; upper: string };
  label?: string;
  [key: string]: any;
}

// --- Component ---
const GeneralConfiguration: React.FC = () => {
  useCheckSessionHeartBeat();
  const location = useLocation();
  const orgDetailsFromNav = location.state?.orgDetails;
  const [orgUid, setOrgUid] = useState<string>("");
  const [generalConfig, setGeneralConfig] = useState<
    Record<string, ConfigItem>
  >({});
  const [updatedConfig, setUpdatedConfig] = useState<Record<string, string>>(
    {}
  );
  const [historyUid, setHistoryUid] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  const beAddress =
    (window as any)._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";
  const privilege = localStorage.getItem("privilege");
  const isSystemAdmin = privilege === "systemadmin";

  useEffect(() => {
    const orgDetailsFromNav = location.state?.orgDetails;
    const orgUidFromNav = orgDetailsFromNav?.org_uid;
    const fetchOrg = async () => {
      try {
        const response = await axios.get(`${beAddress}/api/org-info`, {
          headers: { "user-uid": userUid }, withCredentials: true,
        });

        if (response.status === 200 && response.data.data?.length > 0) {
          let finalOrgUid = orgUidFromNav;

          if (privilege === "orgadmin" && !finalOrgUid) {
            const stored = localStorage.getItem("org_uid");
            if (stored) finalOrgUid = stored;
          }

          if (!finalOrgUid) {
            finalOrgUid = response.data.data[0].org_uid;
          }

          setOrgUid(finalOrgUid);

          const generalConfigData = await fetchGeneralConfig(finalOrgUid);
          await fetchOrgGeneralConfig(finalOrgUid, generalConfigData);
        } else {
          openNotification({ type: "error", message: "No organization found" });
          setLoading(false);
        }
      } catch (err: any) {
        console.error("Error fetching org info:", err);
        openNotification({
          type: "error",
          message: err.message || "Failed to fetch organization",
        });
        setLoading(false);
      }
    };

    const fetchGeneralConfig = async (
      org_uid: string
    ): Promise<Record<string, ConfigItem>> => {
      try {
        const response = await axios.get(
          `${beAddress}/api/general-config-filter`,
          {
            headers: { "user-uid": userUid }, withCredentials: true,
            params: { org_uid },
          }
        );
        if (response.status === 200) {
          setGeneralConfig(response.data.data);
          return response.data.data;
        }
        return {};
      } catch (err: any) {
        console.error("Error fetching general config:", err);
        return {};
      }
    };

    const fetchOrgGeneralConfig = async (
      org_uid: string,
      generalConfigData: Record<string, ConfigItem>
    ) => {
      try {
        const response = await axios.get(
          `${beAddress}/api/org-general-config`,
          {
            headers: { "user-uid": userUid }, withCredentials: true,
            params: { org_uid },
          }
        );

        if (response.status === 200) {
          const orgGeneral = response.data.data?.[0] || {};
          setHistoryUid(orgGeneral.history_uid || "");

          const mergedConfig: Record<string, string> = {};

          Object.values(generalConfigData).forEach((item) => {

            if (!item.db_key) {
              return;
            }
            if (item.db_key === "currency_factor_value") {
              return;
            }

            let val: string | number = orgGeneral[item.db_key] ?? item.default;

            if (item.db_key === "otp_timeout") {
              const apiVal = orgGeneral["otp_session_timeout"];
              if (apiVal) {
                const [h, m] = (apiVal as string).split(":").map(Number);
                val = (h * 60 + m).toString();
              } else {
                val = item.default.toString();
              }
            }

            // App session: convert HH to dropdown string
            if (item.db_key === "app_session_timeout" && val) {
              const [h, m, s] = String(val).split(":").map(Number); // only 3 parts
              if (h && h > 0) val = `${h} hour${h > 1 ? "s" : ""}`;
              else if (m && m > 0) val = `${m} minute${m > 1 ? "s" : ""}`;
              else val = item.default;
            }

            // Web session: convert HH to dropdown string
            if (item.db_key === "web_session_timeout" && val) {
              const [h, m, s] = (val as string).split(":").map(Number);
              if (h === 0 && m === 20) val = "20 minutes";
              else if (h === 0 && m === 40) val = "40 minutes";
              else if (h === 1 && m === 0) val = "1 hour";
              else if (h === 2 && m === 0) val = "2 hours";
              else if (h === 4 && m === 0) val = "4 hours";
              else if (h === 12 && m === 0) val = "12 hours";
              else if (h === 18 && m === 0) val = "18 hours";
              else if (h === 23 && m === 0) val = "23 hours";
              else val = item.default;
            }

            if (item.db_key === "point_to_currency_factor") {
              const currencyObj = orgGeneral[item.db_key];

              if (currencyObj && typeof currencyObj === "object") {
                const code = Object.keys(currencyObj)[0];
                const factorValue = currencyObj[code];

                const label = Object.keys(item.values).find(
                  (k) => item.values[k] === code
                );

                if (label) {
                  mergedConfig["point_to_currency_factor"] = label;
                  mergedConfig["currency_factor_value"] = String(factorValue);
                  return;
                }
              }

              mergedConfig[item.db_key] = item.default;
              mergedConfig["currency_factor_value"] = "";
              return;
            }


            mergedConfig[item.db_key] = String(val ?? item.default);
          });


          setUpdatedConfig(mergedConfig);
        }
      } catch (err: any) {
        console.error("Error fetching org general config:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrg();
  }, [beAddress, sessionUid, userUid]);

  if (loading) return <p>Loading...</p>;

  const handleChange = (key: string, value: string) => {
    //console.log(" handleChange - key:", key, "value:", value); 


    if (key === "otp_timeout" && !/^\d*$/.test(value)) return;


    if (key === "currency_factor_value") {
      if (!/^((0(\.\d{0,})?)|1(\.0*)?)?$/.test(value)) {
        // console.log("Currency factor validation failed"); 
        return;
      }
    }

    setUpdatedConfig((prev) => {
      const updated = { ...prev, [key]: value };
      //console.log(" handleChange - updated state:", updated); // DEBUG
      return updated;
    });
  };



  // const handleChange = (key: string, value: string) => {
  //   if (key === "otp_timeout") {
  //   if (!/^\d*$/.test(value)) return;
  // }

  // if (key === "currency_factor_value") {
  //    if (!/^(0(\.\d*)?|1(\.0*)?)?$/.test(value)) return;
  // }
  //   setUpdatedConfig((prev) => ({ ...prev, [key]: value }));
  // };

  // const handleUpdate = async () => {
  //   try {
  //     const otpValue = Number(updatedConfig["otp_timeout"]);
  //     if (isNaN(otpValue) || otpValue < 2 || otpValue > 10) {
  //       openNotification({
  //         type: "error",
  //         message: "OTP session timeout must be between 2 and 10 minutes",
  //       });
  //       return;
  //     }

  //     const payloadConfig: Record<string, any> = {};

  //     for (const item of Object.values(generalConfig)) {
  //       const value = updatedConfig[item.db_key] ?? item.default;

  //       switch (item.db_key) {
  //         case "otp_timeout":
  //           payloadConfig["otp_session_timeout"] = otpValue;
  //           break;

  //         // case "app_session_timeout":
  //         //   const minutes = appSessionTimeoutToMinutes(value);
  //         //   payloadConfig[item.db_key] = minutes >= 1440
  //         //     ? secondsToDHHMMSS(minutes * 60)
  //         //     : minutesToHHMMSS(minutes);
  //         //   break;

  //         // case "web_session_timeout":
  //         //   const secs = webSessionTimeoutToSeconds(value);
  //         //   payloadConfig[item.db_key] = secs >= 86400 ? secondsToDHHMMSS(secs) : secondsToHHMMSS(secs);
  //         //   break;
  //         case "app_session_timeout":
  //           {
  //             const minutes = appSessionTimeoutToMinutes(value);
  //             const seconds = minutes * 60;
  //             payloadConfig[item.db_key] = seconds;
  //           }
  //           break;

  //         case "web_session_timeout":
  //           {
  //             const secs = webSessionTimeoutToSeconds(value);
  //             payloadConfig[item.db_key] = secs;
  //           }
  //           break;

  //         default:
  //           payloadConfig[item.db_key] = value;
  //           break;
  //       }
  //     }

  //     payloadConfig.history_uid = historyUid;

  //     const payload = {
  //       org_uid: orgUid,
  //       org_general_config: payloadConfig,
  //     };

  //     const response = await axios.put(
  //       `${beAddress}/api/org-general-config`,
  //       payload,
  //       {
  //         headers: { "session-uid": sessionUid, "user-uid": userUid },
  //       }
  //     );

  //     if (response.status === 200 && response.data.status === "Success") {
  //       openNotification({
  //         type: "success",
  //         message:
  //           response.data.message ||
  //           "General configuration updated successfully",
  //       });
  //     } else {
  //       openNotification({
  //         type: "error",
  //         message: response.data.data,
  //       });
  //     }
  //   } catch (err: any) {
  //     console.error("Error updating general config:", err);
  //     openNotification({
  //       type: "error",
  //       message: err.message || "Failed to update general config",
  //     });
  //   }
  // };
  const handleUpdate = async () => {
    try {
      const payloadConfig: Record<string, any> = {};


      const otpValue = Number(updatedConfig["otp_timeout"]);
      if (isNaN(otpValue) || otpValue < 2 || otpValue > 10) {
        openNotification({
          type: "error",
          message: "OTP session timeout must be between 2 and 10 minutes",
        });
        return;
      }


      for (const item of Object.values(generalConfig)) {
        if (!item.db_key) continue;


        if (
          item.db_key === "point_to_currency_factor" ||
          item.db_key === "currency_factor_value"
        )
          continue;

        const value = updatedConfig[item.db_key] ?? item.default;

        switch (item.db_key) {
          case "otp_timeout":
            payloadConfig["otp_session_timeout"] = otpValue;
            break;

          case "app_session_timeout": {
            const minutes = appSessionTimeoutToMinutes(value);
            payloadConfig[item.db_key] = minutes * 60;
            break;
          }

          case "web_session_timeout": {
            const secs = webSessionTimeoutToSeconds(value);
            payloadConfig[item.db_key] = secs;
            break;
          }

          default:
            payloadConfig[item.db_key] = value;
        }
      }


      //console.log("DEBUG - updatedConfig:", updatedConfig); 
      const currencyItem = generalConfig["point_to_currency_factor"];
      const selectedCurrencyLabel = updatedConfig["point_to_currency_factor"] ?? currencyItem?.default;

      //console.log("DEBUG - currencyItem:", currencyItem); 
      //console.log("DEBUG - selectedCurrencyLabel:", selectedCurrencyLabel); 

      if (!selectedCurrencyLabel) {
        openNotification({ type: "error", message: "Please select a currency" });
        return;
      }

      const currencyCode = currencyItem.values[selectedCurrencyLabel];
      //console.log(" DEBUG - currencyCode:", currencyCode); 

      if (!currencyCode) {
        openNotification({
          type: "error",
          message: `Invalid currency "${selectedCurrencyLabel}". Available: ${Object.keys(currencyItem.values).join(', ')}`
        });
        return;
      }

      const factorItem = generalConfig["currency_factor_value"];
      const rawFactorValue = (updatedConfig["currency_factor_value"] || "").trim();
      //console.log("DEBUG - rawFactorValue BEFORE trim:", updatedConfig["currency_factor_value"]); 
      //console.log("DEBUG - rawFactorValue AFTER trim:", rawFactorValue); 

      if (rawFactorValue === "") {
        openNotification({ type: "error", message: "Enter currency factor value" });
        return;
      }



      const factorValue = Number(rawFactorValue);
      if (isNaN(factorValue)) {
        openNotification({ type: "error", message: "Currency factor must be a number" });
        return;
      }

      const lower = Number(factorItem?.limits?.lower ?? 0);
      const upper = Number(factorItem?.limits?.upper ?? 1);

      if (factorValue < lower || factorValue > upper) {
        openNotification({
          type: "error",
          message: `Currency factor must be between ${lower} and ${upper}`,
        });
        return;
      }

      payloadConfig["point_to_currency_factor"] = { [currencyCode]: factorValue };


      payloadConfig.history_uid = historyUid;

      const payload = {
        org_uid: orgUid,
        org_general_config: payloadConfig,
      };

      //console.log(" FINAL PAYLOAD", payload);

      const response = await axios.put(`${beAddress}/api/org-general-config`, payload, {
        headers: { "user-uid": userUid }, withCredentials: true,
      });

      if (response.status === 200 && response.data.status === "Success") {
        openNotification({
          type: "success",
          message: response.data.message || "Configuration updated successfully",
        });
      } else {
        openNotification({
          type: "error",
          message: response.data.data,
        });
      }
    } catch (err: any) {
      console.error("Update error:", err);
      openNotification({
        type: "error",
        message: err.message || "Failed to update configuration",
      });
    }
  };


  const renderControl = (item: ConfigItem) => {
    switch (item.method) {
      case "toggle":
      case "dropdown":
        return (
          <select
            value={updatedConfig[item.db_key] || ""}
            onChange={(e) => handleChange(item.db_key, e.target.value)}
            className="config-select"
            disabled={isSystemAdmin}
          >
            {Object.keys(item.values).map((val) => (
              <option key={val} value={val}>
                {val}
              </option>
            ))}
          </select>
        );
      case "input": {
        const isCurrencyFactor = item.db_key === "currency_factor_value";
        const isOtpTimeout = item.db_key === "otp_timeout";
        const isFloat = item.type === "float" || isCurrencyFactor;

        return (
          <input
            type={isOtpTimeout || isFloat ? "number" : "text"}
            step={isFloat ? "0.01" : "1"}
            min={isFloat ? 0 : undefined}
            max={isFloat ? 1 : undefined}
            value={updatedConfig[item.db_key] || ""}
            onChange={(e) => handleChange(item.db_key, e.target.value)}
            onKeyDown={(e) => {
              if (isFloat && ["e", "E", "+", "-"].includes(e.key)) {
                e.preventDefault();
              }
            }}
            className="config-input"
            disabled={isSystemAdmin}
          />
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="general-config-container">
      <h3 className="general-config-title">General Configuration</h3>
      {Object.values(generalConfig).map((item) => (
        <div key={item.db_key} className="config-item">
          <label className="config-label">{item.name}</label>
          <p className="config-description">{item.description}</p>
          {renderControl(item)}
        </div>
      ))}

      {!isSystemAdmin && (
        <div className="config-actions">
          <button
            className="generalconfig-update-button"
            onClick={handleUpdate}
          >
            Update
          </button>
        </div>
      )}
    </div>
  );
};

export default GeneralConfiguration;
